import { useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Sparkles, ChevronRight, Bell, Award } from 'lucide-react';
import { Button } from '../ui/button';
import { BadgeCelebrationModal } from '../BadgeCelebrationModal';
import { BadgeUnlockToast } from '../BadgeUnlockToast';
import { WeeklyGoalCelebrationModal } from '../dashboard/WeeklyGoalCelebrationModal';
import { BADGE_CATALOG } from '../../utils/badges';

/**
 * AdminPreviewTab — one screen where admins can replay every in-app
 * animation/celebration without needing real user activity. Helpful for
 * QA, design reviews, and onboarding screenshot sessions.
 */

// Workout-count achievements mirror the backend ACHIEVEMENTS dict and the
// user-facing Trophy Case so admins see exactly what users see.
const ACHIEVEMENT_PREVIEW = [
  { id: 'workout_5',   name: 'Getting Started',   description: 'Complete 5 workouts',   icon: '🌟', requirement: 5 },
  { id: 'workout_10',  name: 'Building Momentum', description: 'Complete 10 workouts',  icon: '💪', requirement: 10 },
  { id: 'workout_25',  name: 'Dedicated Athlete', description: 'Complete 30 workouts',  icon: '🔥', requirement: 30 },
  { id: 'workout_50',  name: 'Fitness Warrior',   description: 'Complete 50 workouts',  icon: '⚡', requirement: 50 },
  { id: 'workout_100', name: 'Legend',            description: 'Complete 100 workouts', icon: '👑', requirement: 100 },
];

export const AdminPreviewTab = () => {
  const [badgeCode, setBadgeCode] = useState(null);
  const [weeklyOpen, setWeeklyOpen] = useState(false);
  const [toastBadges, setToastBadges] = useState([]);

  // Trophy Case only shows milestone badges to users; previews should match.
  const milestoneBadges = Object.values(BADGE_CATALOG).filter(
    (b) => (b.category || 'milestone') === 'milestone'
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
      data-testid="admin-preview-tab"
    >
      <div className="bg-card border border-border rounded-2xl p-6">
        <h2 className="font-heading text-2xl text-white uppercase tracking-wider flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          Preview Tools
        </h2>
        <p className="text-muted-foreground text-sm mt-1">
          Replay any user-facing animation to check design, test videos, or grab screenshots.
        </p>
      </div>

      {/* Milestone Badge Celebrations */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="h-5 w-5 text-primary" />
          <h3 className="font-heading text-lg text-primary uppercase tracking-wider">Milestone Celebrations</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {milestoneBadges.map((b) => (
            <button
              key={b.code}
              onClick={() => setBadgeCode(b.code)}
              className="bg-card border border-border rounded-2xl p-4 text-left hover:border-primary/50 transition-colors flex items-center gap-4"
              data-testid={`admin-preview-${b.code}`}
            >
              <img
                src={b.image}
                alt={b.name}
                className="w-14 h-14 object-contain drop-shadow-[0_0_12px_rgba(250,204,21,0.6)]"
              />
              <div className="flex-1 min-w-0">
                <div className="font-heading text-white">{b.name}</div>
                <div className="text-xs text-muted-foreground">{b.description}</div>
                <div className="text-[10px] uppercase tracking-wider mt-1 text-primary">
                  {b.celebration_video ? 'Full video celebration' : 'Toast only'}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </button>
          ))}
        </div>
      </div>

      {/* Workout-Count Achievements (read-only preview — these auto-unlock as
          users hit each lifetime workout threshold on the Trophy Case page). */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Award className="h-5 w-5 text-primary" />
          <h3 className="font-heading text-lg text-primary uppercase tracking-wider">Workout Achievements</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {ACHIEVEMENT_PREVIEW.map((a) => (
            <div
              key={a.id}
              className="bg-card border border-border rounded-2xl p-4 flex items-center gap-4"
              data-testid={`admin-achievement-preview-${a.id}`}
            >
              <div className="h-14 w-14 rounded-xl bg-primary/15 flex items-center justify-center text-3xl shrink-0">
                {a.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-white">{a.name}</div>
                <div className="text-xs text-muted-foreground">{a.description}</div>
                <div className="text-[10px] uppercase tracking-wider mt-1 text-primary">
                  Auto-unlock at {a.requirement} workouts
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Weekly Goal Celebration */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-primary" />
          <h3 className="font-heading text-lg text-primary uppercase tracking-wider">Weekly Goal Celebration</h3>
        </div>
        <div className="bg-card border border-border rounded-2xl p-4 flex items-center justify-between">
          <div>
            <div className="font-heading text-white">Weekly Goal Hit!</div>
            <div className="text-xs text-muted-foreground">Plays when the user logs the final workout of their weekly goal.</div>
          </div>
          <Button
            onClick={() => setWeeklyOpen(true)}
            variant="outline"
            className="border-border text-white hover:bg-white/5"
            data-testid="admin-preview-weekly-goal"
          >
            Play
          </Button>
        </div>
      </div>

      {/* Badge Unlock Toast */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Bell className="h-5 w-5 text-primary" />
          <h3 className="font-heading text-lg text-primary uppercase tracking-wider">Badge Unlock Toast</h3>
        </div>
        <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
          <div className="text-xs text-muted-foreground">
            The small popover that slides in at the top-right when a milestone badge is unlocked (for badges without a video celebration). Auto-dismisses in 6.5s.
          </div>
          <div className="flex gap-2 flex-wrap">
            {milestoneBadges.map((b) => (
              <Button
                key={b.code}
                onClick={() => setToastBadges([b.code])}
                variant="outline"
                size="sm"
                className="border-border text-white hover:bg-white/5"
                data-testid={`admin-preview-toast-${b.code}`}
              >
                Preview {b.name}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <BadgeCelebrationModal
        isOpen={!!badgeCode}
        badgeCode={badgeCode}
        onClose={() => setBadgeCode(null)}
      />

      <WeeklyGoalCelebrationModal
        isOpen={weeklyOpen}
        onClose={() => setWeeklyOpen(false)}
        weeklyGoal={5}
        workoutTitle="Sample Workout Name"
      />

      <BadgeUnlockToast
        badges={toastBadges}
        onDismiss={() => setToastBadges([])}
      />
    </motion.div>
  );
};
